import React, { useState } from "react";
import { setLiveHost } from "../services/userService";
import { firstNameOf } from "./introPauseUtils";

export const isLiveHost = (user) => !!user && user.staffRole === "live_host";

/**
 * Live host role on the member page (founder go 2026-10-12, Events
 * planning). The person hired to host and MC a Curiouzz Live room is STAFF:
 * never dealt an introduction, nobody's candidate, never counted, and their
 * phone (2.5) shows the Live tab alone. Turning the role on also pauses
 * introductions by support; while it stands, Resume is refused everywhere.
 * Turning it off clears the role and resumes them as an ordinary member.
 *
 * Every line here is what the server does (backend api/utils/staffRole.js);
 * if the server changes, change the words with it. The result lives in the
 * PARENT, as the pause panel's does, because the refresh after a change
 * swaps the page for the loader.
 */
const LiveHostPanel = ({ user, canEdit, result, onChanged, onDismissResult, onUnauthorized }) => {
  const host = isLiveHost(user);
  const firstName = firstNameOf(user && user.fullName);
  const who = firstName || "this member";
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const close = () => {
    setOpen(false);
    setNote("");
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const res = await setLiveHost(user._id, { on: !host, note: note.trim() || undefined });
      const data = (res && res.data) || {};
      if (data.success === false) {
        setError(data.message || "The server refused this. Nothing was changed.");
        return;
      }
      close();
      if (onChanged) onChanged({ kind: host ? "host_off" : "host_on", firstName, data });
    } catch (err) {
      const status = err && err.response && err.response.status;
      if (status === 401 && onUnauthorized) {
        onUnauthorized();
        return;
      }
      const serverMessage = err && err.response && err.response.data && err.response.data.message;
      setError(
        serverMessage ||
          (status && status < 500
            ? `The server refused this (HTTP ${status}). Nothing was changed.`
            : "The server did not confirm this. Reload the page to see the current state before trying again.")
      );
    } finally {
      setBusy(false);
    }
  };

  const resultWho = (result && result.firstName) || "this member";
  const closedCount = result && result.data && Array.isArray(result.data.closed) ? result.data.closed.length : 0;

  return (
    <div className="col-md-12 mb-3">
      <div className="border radius-8 px-16 py-12">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div>
            <label className="form-label fw-bold me-2 mb-0">Curiouzz Live host:</label>
            {host ? (
              <span className="bg-info-100 text-info-700 px-8 py-2 radius-4 fw-medium" data-testid="live-host-line">
                Yes. Staff for Curiouzz Live, never introduced.
              </span>
            ) : (
              <span data-testid="live-host-line">No. An ordinary member.</span>
            )}
          </div>
          {canEdit && !open && (
            <button
              type="button"
              className={host ? "btn btn-outline-secondary btn-sm" : "btn btn-outline-info btn-sm"}
              onClick={() => {
                setOpen(true);
                setError("");
              }}
            >
              {host ? "Remove Live host role" : "Make a Live host"}
            </button>
          )}
        </div>

        {open && (
          <form onSubmit={submit} className="mt-12" noValidate>
            {host ? (
              <ul className="text-sm text-secondary-light mb-12 ps-20" style={{ listStyle: "disc", lineHeight: 1.6 }}>
                <li>{who} becomes an ordinary member again: introductions resume now, with no wait added by this page.</li>
                <li>Their phone shows the full app again the next time it opens.</li>
                <li>Nothing is sent to {who}. Tell them yourself.</li>
              </ul>
            ) : (
              <ul className="text-sm text-secondary-light mb-12 ps-20" style={{ listStyle: "disc", lineHeight: 1.6 }}>
                <li className="fw-semibold">For the person hosting a Curiouzz Live room. Staff, not a member.</li>
                <li>
                  {who} is never dealt an introduction and is nobody's candidate, at every door, and is never counted as
                  someone near you.
                </li>
                <li>
                  Introductions are paused by support at the same time. Any open introduction that has not been revealed
                  closes now, as a pause does. Resume is refused everywhere while the role stands; removing the role is
                  what resumes them.
                </li>
                <li>On 2.5 their phone shows the Live tab alone. Older builds hide nothing, but the server still refuses.</li>
                <li>Nothing is sent to {who}. Tell them yourself.</li>
              </ul>
            )}

            <div className="mb-12">
              <label className="form-label mb-4" htmlFor="live-host-note">
                Note (optional, kept in the admin notes)
              </label>
              <textarea
                id="live-host-note"
                className="form-control"
                rows={2}
                maxLength={300}
                placeholder="For example: hired to host the Ottawa room on Nov 14."
                value={note}
                onChange={(e) => {
                  setNote(e.target.value);
                  if (error) setError("");
                }}
                disabled={busy}
              />
              <div className="d-flex justify-content-between mt-1">
                <span className="text-danger text-sm" role={error ? "alert" : undefined}>
                  {error}
                </span>
                <span className="text-xs text-secondary-light">{note.trim().length}/300</span>
              </div>
            </div>

            <div className="d-flex gap-2 justify-content-end">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={close} disabled={busy}>
                Cancel
              </button>
              <button type="submit" className={host ? "btn btn-secondary btn-sm" : "btn btn-info btn-sm"} disabled={busy}>
                {busy ? "Saving..." : host ? "Remove Live host role" : "Make a Live host"}
              </button>
            </div>
          </form>
        )}

        {result && (
          <div className="alert alert-success mt-12 mb-0 py-12 px-16 text-sm" role="status">
            <div className="d-flex justify-content-between gap-2">
              <div>
                {result.kind === "host_on" ? (
                  <>
                    <div className="fw-semibold">
                      {resultWho} is now a Curiouzz Live host{result.data && result.data.already ? " (they already were)" : ""}.
                    </div>
                    <div className="mt-1">
                      {closedCount === 0
                        ? "No open introduction had to close."
                        : closedCount === 1
                        ? "One open introduction closed."
                        : `${closedCount} open introductions closed.`}
                    </div>
                  </>
                ) : (
                  <div className="fw-semibold">
                    {resultWho} is an ordinary member again
                    {result.data && result.data.already ? " (the role was already off)" : ""}. Introductions are on.
                  </div>
                )}
              </div>
              {onDismissResult && (
                <button
                  type="button"
                  className="btn btn-sm p-0 border-0 bg-transparent text-success-600"
                  aria-label="Dismiss"
                  onClick={onDismissResult}
                >
                  ✕
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveHostPanel;
